// Pause, Resume and Finish on a live campaign (Brúnó, 2026-10-15: not only
// delete it). The check before pausing or finishing says what happens in one
// line; the rest is behind the info icon. The line under the campaign's head
// says where a paused or finished one stands.

import {useEffect} from 'react';
import type {BrandCampaign} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {InfoTip} from './info-tip';
import {shortDate} from './format';
import './campaign-run.css';

export type RunAction = 'pause' | 'finish';

/** Creators in it with videos still to hand in. */
function stillWorking(campaign: BrandCampaign): number {
  return campaign.participants.filter((person) => person.reservation_status === 'reserved' || person.reservation_status === 'in_progress').length;
}

/** Until when they can, once it is finished: this month of a recurring one, the due date of a one-time one. */
function deliverBy(campaign: BrandCampaign): string {
  if (campaign.cadence === 'recurring') return ' by the end of this month';
  return campaign.due_at ? ` by ${shortDate(campaign.due_at)}` : '';
}

const creators = (count: number) => `${count} ${count === 1 ? 'creator' : 'creators'}`;

const CREDIT_BACK = 'The credit set aside and not spent comes back once their videos are decided and the 31-day view-bonus period is over.';

export function CampaignRunDialog({campaign, action, busy, onCancel, onConfirm}: {
  campaign: BrandCampaign; action: RunAction; busy: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const working = stillWorking(campaign);
  const pause = action === 'pause';
  return <div className="switch-layer">
    <div className="switch-backdrop" onClick={onCancel} />
    <div className="card switch-dialog run-dialog" role="dialog" aria-modal="true" aria-labelledby="run-title">
      <button className="switch-close" type="button" onClick={onCancel} aria-label="Close"><Icon name="x" size={18} /></button>
      <p className="eyebrow">{pause ? 'Pause campaign' : 'Finish campaign'}</p>
      <h2 id="run-title">{pause ? 'Pause' : 'Finish'} “{campaign.title}”?</h2>
      <p className="switch-note"><Icon name={pause ? 'pause' : 'flag'} size={16} /><span>{pause
        ? 'New creators can’t join or apply until you resume it.'
        : 'New creators can’t join, and it can’t be reopened.'}<InfoTip text={pause
        ? 'Creators already in keep working, chatting and getting paid, and you still review their videos. The public page says it’s closed.'
        : `Creators already in can hand in what they took on, and you still review it. ${campaign.credit ? CREDIT_BACK : ''}`.trim()} /></span></p>
      {working ? <p className="switch-note"><Icon name="users" size={16} /><span>{pause
        ? `${creators(working)} already in keep working.`
        : `${creators(working)} already in can still hand in their videos${deliverBy(campaign)}.`}</span></p> : null}
      <div className="switch-actions">
        <button className="btn btn-light" type="button" onClick={onCancel}>{pause ? 'Keep it live' : 'Keep it'}</button>
        <button className={`btn ${pause ? 'btn-g' : 'btn-danger'}`} type="button" disabled={busy} onClick={onConfirm}>
          {busy ? (pause ? 'Pausing…' : 'Finishing…') : pause ? 'Pause campaign' : 'Finish campaign'}</button>
      </div>
    </div>
  </div>;
}

/** Where a paused or finished campaign stands, under its head; nothing for one that is live or over and settled. */
export function CampaignRunNote({campaign}: {campaign: BrandCampaign}) {
  const working = stillWorking(campaign);
  if (campaign.status === 'paused') {
    return campaign.paused_by === 'team'
      ? <p className="run-note"><Icon name="pause" size={16} /><span><b>Paused by the Earn team.</b> New creators can’t join.<InfoTip text="Creators already in keep working and getting paid. Write to us if you have questions; we resume it once it’s sorted." /></span></p>
      : <p className="run-note"><Icon name="pause" size={16} /><span><b>Paused.</b> New creators can’t join.<InfoTip text="Creators already in keep working, chatting and getting paid. Resume it to take new creators again." /></span></p>;
  }
  if (campaign.status !== 'completed' || (!working && !campaign.credit?.open)) return null;
  return <p className="run-note"><Icon name="flag" size={16} /><span><b>Finished.</b> {working
    ? `${creators(working)} still handing in videos${deliverBy(campaign)}.`
    : 'No new creators join.'}<InfoTip text={campaign.credit ? CREDIT_BACK : 'You still review the videos creators hand in.'} /></span></p>;
}
